import { Typography, List, Theme, ListItemText, ListItemButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import GVCheckbox from 'components/lib/GVCheckbox/GVCheckbox';
import { GraphicsOptionValue } from 'types';
import { MatchingMode, GraphicsSensitivity, GraphicsOptionKey } from '@verify/shared-utils';
import {
  getGraphicsSensitivity,
  getInspectionOriginalOrgId,
  getInspectionSettings,
  getOneToOneSelected,
  inspection,
} from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { useTracker } from 'components/Tracker/TrackerProvider';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GVSearchSelect from 'components/lib/GVSearchSelect/GVSearchSelect';
import { SquarePen } from '@globalvisioninc/assets/icons';
import {
  useCustomProfiles,
  useCustomProfileById,
  useUpdateCustomProfile,
} from 'store/queries/organizations/useCustomProfiles';
import { useUserStateStore } from 'zstore/userStateStore';
import { getOrganizationId, isAdmin, isCustomProfiler, isTenantAdmin } from 'utils/auth';
import useIsInternalOrg from 'hooks/useIsInternalOrg';
import { useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ProfileEditorModal from 'components/common/ProfileEditor/ProfileEditorModal';
import {
  formatGraphicProfileToIni,
  parseIniToProfileFields,
  getEditedFields,
} from 'pages/MyAccount/customProfiles/profileFormatter';
import { ProfileFields } from 'pages/MyAccount/customProfiles/profileFormatter';
import { BarcodeProfileFields } from 'pages/MyAccount/customProfiles/profileFormatter';
import styles from './GraphicsMenu.module.scss';

const useStyles = makeStyles()((theme: Theme) => {
  return {
    checkbox: {
      padding: theme.spacing(0),
      '&.Mui-disabled': {
        opacity: 0.38,
        color: theme.palette.text.secondary,
        '& .MuiIconButton-label:after': {
          display: 'none',
        },
      },
      '& .MuiIconButton-label': {
        position: 'relative',
        zIndex: 0,
      },
      '&.Mui-checked': {
        '& .MuiIconButton-label:after': {
          content: '""',
          left: 4,
          top: 4,
          height: 12,
          width: 12,
          position: 'absolute',
          backgroundColor: 'white',
          zIndex: -1,
        },
      },
    },
  };
});

type CustomProfile = {
  id: string;
  customProfileName: string;
  profileType?: 'graphic' | 'barcode';
};

const GraphicsMenu = () => {
  const { classes } = useStyles();
  const tracker = useTracker();
  const dispatch = useDispatch();
  const oneToOneSelected = useSelector(getOneToOneSelected);
  const enableLENFileMode = useSelector(getInspectionSettings).enableLENFileMode;
  const graphicsSensitivity = useSelector(getGraphicsSensitivity);
  const originalOrgId = useSelector(getInspectionOriginalOrgId);
  const { user } = useUserStateStore();
  const userOrgId = getOrganizationId(user);
  const featureFlags = useFlags();
  const isInternalOrg = useIsInternalOrg();
  const [tooltipContainer, setTooltipContainer] = useState<HTMLDivElement | null>(null);
  const canEditProfiles =
    isCustomProfiler(user) ||
    ((isAdmin(user) || isTenantAdmin(user)) && (isInternalOrg || featureFlags.showUsersCustomProfiles));
  const updateCustomProfileMutation = useUpdateCustomProfile();

  const [profileIdToEdit, setProfileIdToEdit] = useState<string | undefined>(undefined);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { data: profileToEdit } = useCustomProfileById(profileIdToEdit);

  const handleEditClick = (profileId: string) => {
    setProfileIdToEdit(profileId);
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
    setProfileIdToEdit(undefined);
  };

  const handleEditSave = (profileName: string, fields: ProfileFields | BarcodeProfileFields) => {
    if (!profileToEdit) return;
    const profileString = formatGraphicProfileToIni(fields as ProfileFields);
    updateCustomProfileMutation.mutate(
      {
        ...profileToEdit,
        customProfileName: profileName,
        customProfileString: profileString,
      },
      {
        onSuccess: () => {
          const originalFields = parseIniToProfileFields(profileToEdit.customProfileString ?? '');
          tracker.track({
            name: 'custom-profile-edited',
            data: {
              profileId: profileToEdit.id,
              profileName,
              profileType: 'graphic',
              fieldsEdited: getEditedFields(fields, originalFields),
            },
          });
          handleEditClose();
        },
      },
    );
  };

  // Check if inspection is cloned from a different organization
  const isClonedFromDifferentOrg = useMemo(() => {
    if (!originalOrgId || !userOrgId) {
      return false;
    }
    return originalOrgId !== userOrgId;
  }, [originalOrgId, userOrgId]);

  // Get additional profile ID when cloned from different org
  const additionalProfileId = useMemo(() => {
    if (!isClonedFromDifferentOrg || !graphicsSensitivity) {
      return undefined;
    }
    // Check if graphicsSensitivity is a custom profile ID (not 'Standard' or 'High')
    if (graphicsSensitivity !== 'Standard' && graphicsSensitivity !== 'High') {
      return graphicsSensitivity;
    }
    return undefined;
  }, [isClonedFromDifferentOrg, graphicsSensitivity]);

  const { customProfilesList } = useCustomProfiles(undefined, additionalProfileId);

  // Filter to only show graphic profiles
  const graphicProfiles = useMemo(() => {
    return customProfilesList.filter((profile: CustomProfile) => (profile.profileType || 'graphic') === 'graphic');
  }, [customProfilesList]);

  const sensitivityOptions = [
    { value: 'Standard', label: 'Standard' },
    { value: 'High', label: 'High' },
  ];
  const hasCustomProfiles = graphicProfiles.length > 0;

  const handleCheckboxChange = () => {
    const updatedValue = oneToOneSelected ? MatchingMode.OneToMany : MatchingMode.OneToOne;

    dispatch(
      inspection.actions.setGraphicsOptions({
        key: GraphicsOptionKey.MatchingMode,
        value: updatedValue as GraphicsOptionValue,
      }),
    );
  };

  const handleEnableLENFileModeChange = () => {
    dispatch(inspection.actions.setInspectionSettings({ enableLENFileMode: !enableLENFileMode }));
  };

  const handleSensitivityChange = (selectedValue: string) => {
    let sensitivityValue: GraphicsOptionValue | string;

    switch (selectedValue) {
      case 'Standard':
        sensitivityValue = GraphicsSensitivity.STANDARD;
        break;
      case 'High':
        sensitivityValue = GraphicsSensitivity.HIGH;
        break;
      default:
        sensitivityValue = selectedValue as GraphicsOptionValue;
        break;
    }

    dispatch(
      inspection.actions.setGraphicsOptions({
        key: GraphicsOptionKey.Sensitivity,
        value: sensitivityValue,
      }),
    );
  };

  return (
    <>
      <div className={styles.menu} ref={setTooltipContainer}>
        <List className={styles.list} dense>
          <GVSearchSelect
            value={graphicsSensitivity ?? 'Standard'}
            label="Sensitivity"
            onValueChange={handleSensitivityChange}
            searchPlaceholder="Search profiles..."
            sections={[
              {
                label: 'Sensitivity',
                searchable: false,
                options: sensitivityOptions,
              },
              ...(hasCustomProfiles
                ? [
                    {
                      label: 'Custom Profiles',
                      options: graphicProfiles.map((p: CustomProfile) => ({
                        value: p.id,
                        label: p.customProfileName,
                        ...(canEditProfiles
                          ? {
                              action: {
                                icon: SquarePen,
                                label: `Edit ${p.customProfileName}`,
                                onClick: () => handleEditClick(p.id),
                              },
                            }
                          : {}),
                      })),
                    },
                  ]
                : []),
            ]}
          />
          <div>
            <ListItemButton onClick={() => handleCheckboxChange()}>
              <GVCheckbox
                className={classes.checkbox}
                checked={oneToOneSelected}
                onClick={(e) => {
                  e.preventDefault();
                }}
              />
              <ListItemText
                primary={
                  <Typography className={styles.label}>
                    {'Pages in same order for faster inspection'}
                    <GVTooltip
                      title="Verify Compare can process your inspection faster by matching like pages together.
                    ONLY select this option if the sort order is exactly the same —otherwise the inspection will fail."
                      side="top"
                      align="end"
                      container={tooltipContainer}
                      className={styles.tooltip}
                    >
                      <InfoOutlinedIcon className={styles.icon} />
                    </GVTooltip>
                  </Typography>
                }
              />
            </ListItemButton>
          </div>
          <div>
            <ListItemButton onClick={handleEnableLENFileModeChange}>
              <GVCheckbox
                className={classes.checkbox}
                checked={!!enableLENFileMode}
                data-testid="enable_len_mode"
                onClick={(e) => {
                  e.preventDefault();
                }}
              />
              <ListItemText
                primary={
                  <Typography className={styles.label}>
                    Enable RIP Comparison
                    <GVTooltip
                      title="Compare reference PDF artwork with post-RIP plate files (LEN/TIFF) in black and white. Distortion is corrected as needed."
                      side="top"
                      align="center"
                      container={tooltipContainer}
                      className={styles.tooltip}
                    >
                      <InfoOutlinedIcon className={styles.icon} />
                    </GVTooltip>
                  </Typography>
                }
              />
            </ListItemButton>
          </div>
        </List>
      </div>
      {canEditProfiles && (
        <ProfileEditorModal
          open={editModalOpen}
          onClose={handleEditClose}
          onSave={handleEditSave}
          profile={profileToEdit ?? null}
          profileType="graphic"
          initialProfileName={profileToEdit?.customProfileName ?? ''}
        />
      )}
    </>
  );
};

export default GraphicsMenu;
